import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AdminPages.css';

const emptyForm = {
  name: '',
  category: '',
  brand: '',
  materialType: '',
  priceValue: '',
  price: '',
  image: ''
};

const Materials = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fetchError, setFetchError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  useEffect(() => {
    fetchMaterials();
  }, []);

  const fetchMaterials = async () => {
    setFetchError('');
    try {
      const res = await axios.get('/api/admin/materials');
      setMaterials(res.data);
    } catch (err) {
      console.error('Error fetching materials:', err);
      setFetchError(err.response?.data?.message || 'Failed to load materials. Check server connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));

    if (name === 'priceValue') {
      const numVal = parseFloat(value);
      if (!isNaN(numVal)) {
        setForm(prev => ({ ...prev, priceValue: value, price: `₹${numVal} sqm` }));
      }
    }
  };

  const openAddForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setError('');
    setShowForm(true);
  };

  const openEditForm = (material) => {
    setForm({
      name: material.name,
      category: material.category,
      brand: material.brand,
      materialType: material.materialType,
      priceValue: material.priceValue.toString(),
      price: material.price,
      image: material.image || ''
    });
    setEditingId(material._id);
    setError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.name || !form.category || !form.brand || !form.materialType || !form.priceValue || !form.price) {
      setError('Please fill in all required fields.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        priceValue: parseFloat(form.priceValue)
      };

      if (editingId) {
        await axios.put(`/api/admin/materials/${editingId}`, payload);
      } else {
        await axios.post('/api/admin/materials', payload);
      }
      closeForm();
      fetchMaterials();
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving material');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`/api/admin/materials/${id}`);
      fetchMaterials();
    } catch (error) {
      console.error('Error deleting material:', error);
      alert('Error deleting material');
    }
  };

  const categories = [...new Set(materials.map(m => m.category))].sort();

  const filtered = materials.filter(m => {
    const matchesSearch = !searchTerm ||
      m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.brand.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !filterCategory || m.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return <div className="admin-loading">Loading...</div>;
  }

  return (
    <div className="admin-page admin-page-wide">
      {fetchError && (
        <div style={{ background: '#fef2f2', color: '#dc2626', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', border: '1px solid #fecaca' }}>
          {fetchError} <button onClick={fetchMaterials} style={{ marginLeft: '12px', cursor: 'pointer', background: '#dc2626', color: '#fff', border: 'none', padding: '4px 12px', borderRadius: '4px' }}>Retry</button>
        </div>
      )}
      <div className="admin-page-header">
        <div>
          <h2 className="admin-page-title">Materials</h2>
          <p className="admin-page-desc">
            Manage catalog materials, categories, and pricing. ({materials.length} total)
          </p>
        </div>
        <button className="admin-btn admin-btn-primary" onClick={openAddForm}>
          + Add Material
        </button>
      </div>

      {/* Filters */}
      <div className="admin-filters">
        <input
          type="text"
          placeholder="Search by name or brand..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="admin-filter-input"
        />
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="admin-filter-select"
        >
          <option value="">All Categories</option>
          {categories.map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      {/* Modal Form */}
      {showForm && (
        <div className="admin-modal-overlay" onClick={closeForm}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>{editingId ? 'Edit Material' : 'Add New Material'}</h3>
              <button className="admin-modal-close" onClick={closeForm}>&times;</button>
            </div>
            <form onSubmit={handleSubmit} className="admin-material-form">
              {error && <div className="admin-form-error">{error}</div>}

              <div className="admin-form-grid">
                <div className="admin-form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="e.g. Calacatto Gold Marble"
                    required
                  />
                </div>
                <div className="admin-form-group">
                  <label>Category *</label>
                  <input
                    type="text"
                    name="category"
                    value={form.category}
                    onChange={handleChange}
                    placeholder="e.g. Natural Stone"
                    required
                    list="category-list"
                  />
                  <datalist id="category-list">
                    {categories.map(c => <option key={c} value={c} />)}
                  </datalist>
                </div>
                <div className="admin-form-group">
                  <label>Brand *</label>
                  <input
                    type="text"
                    name="brand"
                    value={form.brand}
                    onChange={handleChange}
                    placeholder="e.g. Artisindustry"
                    required
                  />
                </div>
                <div className="admin-form-group">
                  <label>Material Type *</label>
                  <input
                    type="text"
                    name="materialType"
                    value={form.materialType}
                    onChange={handleChange}
                    placeholder="e.g. Marble"
                    required
                  />
                </div>
                <div className="admin-form-group">
                  <label>Price Value *</label>
                  <input
                    type="number"
                    name="priceValue"
                    value={form.priceValue}
                    onChange={handleChange}
                    placeholder="e.g. 145"
                    required
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="admin-form-group">
                  <label>Price Display *</label>
                  <input
                    type="text"
                    name="price"
                    value={form.price}
                    onChange={handleChange}
                    placeholder="e.g. ₹145 sqm"
                    required
                  />
                </div>
              </div>

              <div className="admin-form-group admin-form-full">
                <label>Image URL</label>
                <input
                  type="text"
                  name="image"
                  value={form.image}
                  onChange={handleChange}
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              {form.image && (
                <div className="admin-image-preview">
                  <img src={form.image} alt="Preview" onError={(e) => { e.target.style.display = 'none'; }} />
                </div>
              )}

              <div className="admin-form-actions">
                <button type="button" className="admin-btn admin-btn-secondary" onClick={closeForm}>
                  Cancel
                </button>
                <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editingId ? 'Update Material' : 'Add Material'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Materials Table */}
      <div className="admin-page-card admin-table-card">
        <div className="admin-notifications-table">
          <table>
            <thead>
              <tr>
                <th>Image</th>
                <th>Name</th>
                <th>Category</th>
                <th>Brand</th>
                <th>Type</th>
                <th>Price</th>
                <th>Added</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="8" className="admin-no-data">
                    {searchTerm || filterCategory
                      ? 'No materials match your filters.'
                      : 'No materials yet. Click "Add Material" to create one.'}
                  </td>
                </tr>
              ) : (
                filtered.map((m) => (
                  <tr key={m._id}>
                    <td>
                      {m.image ? (
                        <img src={m.image} alt={m.name} className="admin-material-thumb" />
                      ) : (
                        <span className="admin-no-thumb">--</span>
                      )}
                    </td>
                    <td><strong>{m.name}</strong></td>
                    <td>
                      <span className="admin-category-badge">{m.category}</span>
                    </td>
                    <td>{m.brand}</td>
                    <td>{m.materialType}</td>
                    <td><strong>{m.price}</strong></td>
                    <td>{new Date(m.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="admin-action-buttons">
                        <button
                          className="admin-btn-icon admin-btn-edit"
                          onClick={() => openEditForm(m)}
                          title="Edit"
                        >
                          Edit
                        </button>
                        <button
                          className="admin-btn-icon admin-btn-delete"
                          onClick={() => handleDelete(m._id, m.name)}
                          title="Delete"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Materials;
